import React, { useMemo } from "react";

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}

const ELLIPSIS = "…";

/**
 * First page, last page, and a window around the current one. The previous
 * version always listed pages 1-5 plus the last two, so the middle of a long
 * list could only be reached by clicking Next over and over.
 */
function buildPages(currentPage: number, totalPages: number): (number | string)[] {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, index) => index + 1);
  }

  const pages: (number | string)[] = [1];
  const start = Math.max(2, currentPage - 1);
  const end = Math.min(totalPages - 1, currentPage + 1);

  if (start > 2) pages.push(`${ELLIPSIS}-left`);
  for (let page = start; page <= end; page++) pages.push(page);
  if (end < totalPages - 1) pages.push(`${ELLIPSIS}-right`);

  pages.push(totalPages);
  return pages;
}

const arrowClass =
  "inline-flex items-center rounded-lg border border-white/10 px-3 py-2 text-sm text-slate-300 transition hover:border-portal-400/60 hover:text-white disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-white/10 disabled:hover:text-slate-300";

const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalPages,
  onPageChange,
}) => {
  const pages = useMemo(
    () => buildPages(currentPage, totalPages),
    [currentPage, totalPages]
  );

  if (totalPages <= 1) return null;

  return (
    <nav
      className="mt-10 flex flex-wrap items-center justify-center gap-2"
      aria-label="Pagination"
    >
      <button
        type="button"
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage === 1}
        className={arrowClass}
      >
        Previous
      </button>

      {pages.map((page) =>
        typeof page === "string" ? (
          <span key={page} className="px-2 text-slate-500">
            {ELLIPSIS}
          </span>
        ) : (
          <button
            key={page}
            type="button"
            onClick={() => onPageChange(page)}
            aria-current={page === currentPage ? "page" : undefined}
            className={`min-w-[2.5rem] rounded-lg border px-3 py-2 text-sm font-medium transition ${
              page === currentPage
                ? "border-portal-400 bg-portal-400/15 text-portal-400"
                : "border-white/10 text-slate-300 hover:border-portal-400/60 hover:text-white"
            }`}
          >
            {page}
          </button>
        )
      )}

      <button
        type="button"
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
        className={arrowClass}
      >
        Next
      </button>
    </nav>
  );
};

export default Pagination;
